import { AbiIERC20 } from './abis';
import type { AssetType } from '$types/assetType';
import { type Abi, zeroAddress } from 'viem';
import type { AssetTypeIdentifier } from '$types/assetTypeIdentifier';
import { TOKEN_ADDRESSES_ENTITIES } from '$lib/constants/tokenAddresses';
import type { Address } from '$types/address';

import IconCRC from '$lib/assets/asset-types/CRC.webp';
import IconEUR from '$lib/assets/asset-types/EUR.webp';
import IconGBP from '$lib/assets/asset-types/GBP.webp';
import IconGNO from '$lib/assets/asset-types/GNO.webp';
import IconUSD from '$lib/assets/asset-types/USD.webp';
import IconUSDOld from '$lib/assets/asset-types/USD_OLD.webp';

import IconGBPe from '$lib/assets/asset-types/GBPe.webp';
import IconEURe from '$lib/assets/asset-types/EURe.webp';
import IconXDAI from '$lib/assets/asset-types/XDAI.webp';
import IconUSDC from '$lib/assets/asset-types/USDC.webp';

import IconETH from '$lib/assets/asset-types/ETH.webp';
import IconWBTC from '$lib/assets/asset-types/BTC.webp';
import IconSDAI from '$lib/assets/asset-types/SDAI.webp';
import { TransferType } from '$types/transaction';

const XDAI: AssetType = {
  id: 'xdai',
  icon: IconUSD,
  originalIcon: IconXDAI,
  class: 'native',
  fiatCurrencyIdentifier: 'usd',
  fiatCurrencySymbol: '$',
  name: 'xDAI',
  symbol: 'xDAI',
  decimals: 18,
  coingecko_id: 'xdai',
  swap: true,
  display: true,
  include: 'wxdai',
  swapsIOid: '100',
  description:
    'xDai, the native token of Gnosis Chain, is a stablecoin designed to maintain a 1:1 peg with the U.S. dollar',
  link: 'https://docs.gnosischain.com/about/tokens/xdai',
  digits: 2,
};

const WXDAI: AssetType = {
  id: 'wxdai',
  class: 'erc20',
  fiatCurrencyIdentifier: 'usd',
  fiatCurrencySymbol: '$',
  contractAddress: TOKEN_ADDRESSES_ENTITIES.wxdai,
  name: 'wxDAI',
  symbol: 'wxDAI',
  display: false,
  decimals: 18,
  swapsIOid: '100.wxdai',
  digits: 2,
};

const EURE: AssetType = {
  id: 'eure',
  icon: IconEUR,
  originalIcon: IconEURe,
  class: 'erc20',
  name: 'Monerium EUR',
  fiatCurrencyIdentifier: 'eur',
  fiatCurrencySymbol: '€',
  symbol: 'EURe',
  decimals: 18,
  abi: AbiIERC20 as Abi,
  coingecko_id: 'monerium-eur-money',
  contractAddress: TOKEN_ADDRESSES_ENTITIES.eure,
  sponsored: true,
  display: true,
  swap: true,
  swapsIOid: '100.eure',
  description:
    'Monerium EURe is a regulated euro stablecoin, offering seamless transactions between bank accounts and the blockchain. Pegged 1:1 to the euro, EURe enables fast transfers and easy redemption',
  link: 'https://monerium.com/about',
  digits: 2,
};

const GBPE: AssetType = {
  id: 'gbpe',
  icon: IconGBP,
  originalIcon: IconGBPe,
  class: 'erc20',
  name: 'Monerium GBP',
  fiatCurrencyIdentifier: 'gbp',
  fiatCurrencySymbol: '£',
  symbol: 'GBPe',
  decimals: 18,
  abi: AbiIERC20 as Abi,
  coingecko_id: 'monerium-gbp-emoney',
  contractAddress: TOKEN_ADDRESSES_ENTITIES.gbpe,
  sponsored: true,
  display: true,
  swap: true,
  swapsIOid: '100.gbpe',
  description:
    'Monerium GBPe is a regulated pound sterling stablecoin, offering seamless transactions between bank accounts and the blockchain. Pegged 1:1 to the pound, GBPe enables fast transfers and easy redemption',
  link: 'https://monerium.com/about',
  digits: 2,
};

const CRC: AssetType = {
  id: 'crc',
  icon: IconCRC,
  class: 'circles',
  name: 'Circles',
  symbol: 'CRC',
  decimals: 18,
  display: true,
  sponsored: true,
  description:
    'Circles is a unique monetary system that allows individuals to create and exchange their personal currency under common rules.',
  link: 'https://aboutcircles.com',
  swap: true,
  digits: 2,
};

const GNO: AssetType = {
  id: 'gno',
  icon: IconGNO,
  class: 'erc20',
  contractAddress: TOKEN_ADDRESSES_ENTITIES.gno,
  name: 'Gnosis Token',
  symbol: 'GNO',
  display: true,
  decimals: 18,
  abi: AbiIERC20 as Abi,
  coingecko_id: 'gnosis',
  sponsored: true,
  swapsIOid: '100.gno',
  description:
    "GNO is the key token of the Gnosis ecosystem. It's used for staking on the Gnosis Beacon Chain and acts as the governance token for the GnosisDAO.",
  link: 'https://docs.gnosischain.com/about/tokens/gno',
  swap: true,
  digits: 4,
};

const USDC: AssetType = {
  id: 'usdc',
  icon: IconUSD,
  originalIcon: IconUSDC,
  class: 'erc20',
  fiatCurrencyIdentifier: 'usd',
  fiatCurrencySymbol: '$',
  contractAddress: TOKEN_ADDRESSES_ENTITIES.usdc,
  name: 'USDC.e',
  symbol: 'USDC.e',
  display: true,
  decimals: 6,
  abi: AbiIERC20 as Abi,
  coingecko_id: 'usd-coin',
  swap: true,
  sponsored: true,
  swapsIOid: '100.usdc.e',
  description:
    'USDC.e is the token version that follows the Circle standard and will be used to bridge USDC via CCTP in the future',
  link: 'https://bridge.gnosischain.com/usdc',
};

const USDC_OLD: AssetType = {
  id: 'usdc-old',
  icon: IconUSDOld,
  originalIcon: IconUSDC,
  class: 'erc20',
  fiatCurrencyIdentifier: 'usd',
  fiatCurrencySymbol: '$',
  contractAddress: TOKEN_ADDRESSES_ENTITIES['usdc-old'],
  name: 'USD//C',
  symbol: 'USDC (old)',
  decimals: 6,
  abi: AbiIERC20 as Abi,
  coingecko_id: 'gnosis-xdai-bridged-usdc-gnosis',
  swap: true,
  display: true,
  sponsored: true,
  description: 'USDC (old) is the wrapped version of the token minted by the Omnibridge',
  link: 'https://docs.gnosischain.com/about/tokens/xdai',
};

const WETH: AssetType = {
  id: 'weth',
  icon: IconETH,
  class: 'erc20',
  contractAddress: TOKEN_ADDRESSES_ENTITIES.weth,
  name: 'Wrapped Ether',
  symbol: 'WETH',
  display: false,
  decimals: 18,
  abi: AbiIERC20 as Abi,
  coingecko_id: 'gnosis-xdai-bridged-weth-gnosis-chain',
  sponsored: true,
  swapsIOid: '100.weth',
  description:
    'Wrapped Ether (WETH) is a token that represents Ether (ETH) in a format that is compatible with the ERC20 standard',
  link: 'https://weth.io/',
};

const WBTC: AssetType = {
  id: 'wbtc',
  icon: IconWBTC,
  class: 'erc20',
  contractAddress: TOKEN_ADDRESSES_ENTITIES.wbtc,
  name: 'Wrapped Bitcoin',
  symbol: 'WBTC',
  display: false,
  decimals: 8,
  abi: AbiIERC20 as Abi,
  coingecko_id: 'wrapped-bitcoin',
  sponsored: true,
  swapsIOid: '100.wbtc',
  description:
    'Wrapped Bitcoin (WBTC) is an ERC20 token backed 1:1 with Bitcoin, bringing Bitcoins liquidity to Gnosis Chain',
  link: 'https://wbtc.network/',
};

const SDAI: AssetType = {
  id: 'sdai',
  icon: IconSDAI,
  class: 'erc20',
  contractAddress: TOKEN_ADDRESSES_ENTITIES.sdai,
  name: 'Savings DAI',
  symbol: 'sDAI',
  display: false,
  decimals: 18,
  abi: AbiIERC20 as Abi,
  coingecko_id: 'savings-dai',
  sponsored: true,
  swapsIOid: '100.sdai',
  description:
    'Savings DAI (sDAI) is an interest-bearing version of DAI, automatically earning yield from the Dai Savings Rate',
  link: 'https://docs.makerdao.com/build/dai.js/savingsservice',
};

export const ASSET_TYPES: readonly AssetType[] = [
  CRC,
  EURE,
  GBPE,
  USDC,
  GNO,
  USDC_OLD,
  WETH,
  WBTC,
  SDAI,
  XDAI,
  WXDAI,
] as const;

export const VISIBLE_ASSET_TYPES: readonly AssetType[] = ASSET_TYPES.filter(
  (assetType) => assetType.display,
);

export const ASSET_TYPES_ENTITIES: Record<AssetTypeIdentifier, AssetType> = ASSET_TYPES.reduce(
  (accumulator, assetType) => ({
    ...accumulator,
    [assetType.id]: assetType,
  }),
  {} as unknown as Record<AssetTypeIdentifier, AssetType>,
);

export const getAssetInEnvioContext = ({
  tokenAddress,
  transferType,
  wrappedGcrcAddress,
}: {
  tokenAddress: Address;
  transferType?: TransferType;
  wrappedGcrcAddress?: Address;
}): AssetType | undefined => {
  if (tokenAddress === zeroAddress) {
    return ASSET_TYPES_ENTITIES.xdai;
  }

  if (wrappedGcrcAddress && tokenAddress === wrappedGcrcAddress) {
    return ASSET_TYPES_ENTITIES.crc;
  }

  const asset = ASSET_TYPES.find((asset) => asset.contractAddress === tokenAddress);

  if (!asset && transferType !== TransferType.MetriTransfer) {
    return ASSET_TYPES_ENTITIES.crc;
  }

  return asset;
};
